"use client"

import {
  type ReactNode,
  useState,
  useContext,
  createContext,
  useCallback,
  useEffect,
} from "react"
import type { Session } from "./types"
import { signout } from "#/lib/api/client/signout"
import { socialSignalAxiosInstance } from "#/lib/social-signal-api-client"

// React hooks and context
const SessionContext = createContext<{
  session: Session | null
  isPending: boolean
} | null>(null)
const CreateSessionContext = createContext<(newSession: Session) => void>(
  () => {},
)
const InvalidateSessionContext = createContext<() => Promise<void>>(
  async () => {},
)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, _setSession] = useState<Session | null>(null)
  const [isPending, setIsPending] = useState(true)

  const setSession = useCallback((newSession: Session | null) => {
    _setSession(newSession)
    if (newSession) {
      socialSignalAxiosInstance.defaults.headers.common.Authorization = `Bearer ${newSession.accessToken}`
      storeSession(newSession)
    } else {
      socialSignalAxiosInstance.defaults.headers.common.Authorization =
        undefined
      clearStoredSession()
    }
  }, [])

  useEffect(() => {
    const storedSession = getStoredSession()
    if (storedSession) {
      setSession(storedSession)
    }
    setIsPending(false)
  }, [setSession])

  const invalidateSession = useCallback(async () => {
    try {
      // Call the backend signout endpoint
      await signout()
    } catch (error) {
      // Even if the backend call fails, clear the local session
      console.error("Failed to sign out on backend:", error)
    }
    setSession(null)
  }, [setSession])

  return (
    <SessionContext.Provider value={{ session, isPending }}>
      <CreateSessionContext.Provider value={setSession}>
        <InvalidateSessionContext.Provider value={invalidateSession}>
          {children}
        </InvalidateSessionContext.Provider>
      </CreateSessionContext.Provider>
    </SessionContext.Provider>
  )
}

export function useSession() {
  const context = useContext(SessionContext)
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider")
  }
  return context
}

export function useCreateSession() {
  const context = useContext(CreateSessionContext)
  if (!context) {
    throw new Error("useCreateSession must be used within a SessionProvider")
  }
  return context
}

export function useInvalidateSession() {
  const context = useContext(InvalidateSessionContext)
  if (!context) {
    throw new Error(
      "useInvalidateSession must be used within a SessionProvider",
    )
  }
  return context
}

// Session storage utilities
const SESSION_STORAGE_KEY = "social_signal_session"

function getStoredSession(): Session | null {
  if (typeof window === "undefined") return null

  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (!stored) return null

    const parsed = JSON.parse(stored)
    // Check if session is expired
    if (new Date(parsed.expiresAt) <= new Date()) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
      return null
    }

    return {
      ...parsed,
      expiresAt: new Date(parsed.expiresAt),
    }
  } catch (error) {
    console.error(error)
    sessionStorage.removeItem(SESSION_STORAGE_KEY)
    return null
  }
}

function storeSession(session: Session): void {
  if (typeof window === "undefined") return
  sessionStorage.setItem(
    SESSION_STORAGE_KEY,
    JSON.stringify({
      ...session,
      expiresAt: session.expiresAt.toISOString(),
    }),
  )
}

function clearStoredSession(): void {
  if (typeof window === "undefined") return
  sessionStorage.removeItem(SESSION_STORAGE_KEY)
}
